/**
 * Vue 3 Composable for Socket.IO Connection Management
 * Consolidates 30+ socket functions from vanilla JS codebase
 * Provides reactive connection state and TypeScript-safe event handling
 */

import { ref, type Ref } from 'vue'
import { io, type Socket } from 'socket.io-client'
import { Logger, LogCategory } from '../utils/Logger.class'

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Socket connection state
 */
export interface SocketState {
  connected: boolean
  connecting: boolean
  reconnectAttempts: number
  socketId: string | null
}

/**
 * Socket event names (string literal union for type safety)
 */
export type SocketEventName =
  | 'connect'
  | 'disconnect'
  | 'connect_error'
  | 'game_state'
  | 'game_state_update'
  | 'room_joined'
  | 'room_created'
  | 'game_reconnected'
  | 'player_joined'
  | 'player_left'
  | 'player_reconnected'
  | 'bot_added'
  | 'meld_holder_set'
  | 'game_started'
  | 'turn_change'
  | 'round_end'
  | 'round_ended'
  | 'game_end'
  | 'game_paused'
  | 'paused_game_lobby'
  | 'paused_lobby_updated'
  | 'rematch_started'
  | 'action_result'
  | 'profile_updated'
  | 'team_name_updated'
  | 'error'
  | 'join_request'
  | 'awaiting_join_approval'
  | 'show_seat_selection'
  | 'join_denied'
  | 'join_cancelled'
  | 'seat_assigned'
  | 'seat_assignment_complete'
  | 'seat_changed'
  | 'player_kicked'
  | 'kicked'
  | 'joined_as_spectator'
  | 'spectator_joined'
  | 'spectator_list'
  | 'spectator_admitted'
  | 'admitted_to_seat'
  | 'reconnection_request'
  | 'awaiting_reconnection_approval'
  | 'reconnection_denied'
  | 'creator_action_request_received'
  | 'creator_action_response_received'
  | 'player_replaced_with_bot'
  | 'player_temporarily_disconnected'
  | 'player_truly_disconnected'
  | 'game_closed_for_all'
  | 'webrtc_offer'
  | 'webrtc_answer'
  | 'webrtc_ice_candidate'
  | 'player_video_state'
  | 'player_video_states'
  | 'player_activity'
  | 'player_nudged'
  | 'stale_game_recovery'
  | 'recovery_complete'
  | 'pile_recycled'
  | 'round_ended_auto'
  | 'chat_message'
  | 'chat_history'

/**
 * Event payload types
 */
export interface GameStatePayload {
  room_code: string
  players: any[]
  current_player_index: number
  [key: string]: any
}

export interface RoomCreatedPayload {
  room_code: string
  player_id: string
}

export interface PlayerJoinedPayload {
  player_id: string
  username: string
  display_name?: string
  profile_picture?: string
}

export interface ActionResultPayload {
  action: string
  success: boolean
  message?: string
  [key: string]: any
}

export interface ErrorPayload {
  message: string
  code?: string
}

export interface VideoStatePayload {
  player_id: string
  video_enabled: boolean
  audio_enabled: boolean
}

export interface WebRTCOfferPayload {
  from_player_id: string
  offer: RTCSessionDescriptionInit
}

export interface WebRTCAnswerPayload {
  from_player_id: string
  answer: RTCSessionDescriptionInit
}

export interface WebRTCIceCandidatePayload {
  from_player_id: string
  candidate: RTCIceCandidateInit
}

/**
 * Meld data structure for play_melds action
 */
export interface MeldData {
  card_indices: number[]
  is_wild_meld?: boolean
  is_hidden_canasta?: boolean
}

/**
 * GIF data for chat messages
 */
export interface ChatGifData {
  url: string
  preview_url: string
  width: number
  height: number
  id: string
}

/**
 * Chat message payload
 */
export interface ChatMessagePayload {
  id: string
  player_id: string
  player_name: string
  message: string
  timestamp: number
  seat_position: number
  gif?: ChatGifData  // Optional GIF attachment
}

/**
 * Chat history payload (sent on reconnection)
 */
export interface ChatHistoryPayload {
  messages: ChatMessagePayload[]
}

/**
 * Room creation options
 */
export interface CreateRoomOptions {
  username: string
  userId?: string | null
  teamName?: string
  displayName?: string | null
  profilePicture?: string | null
  gameMode?: 'bots' | 'humans'
}

/**
 * Join room options
 */
export interface JoinRoomOptions {
  roomCode: string
  username: string
  userId?: string | null
  displayName?: string | null
  profilePicture?: string | null
}

/**
 * Reconnect room options
 */
export interface ReconnectRoomOptions {
  roomCode: string
  username: string
  userId?: string | null
  profilePicture?: string | null
}

// ============================================================================
// Constants
// ============================================================================

const MAX_RECONNECT_ATTEMPTS = 25
const DEFAULT_TIMEOUT = 10000
const SHORT_TIMEOUT = 5000
const RECONNECT_BASE_DELAY = 2000
const RECONNECT_MAX_DELAY = 10000

/**
 * Get Socket.IO connection URL based on environment
 */
function getSocketURL(): string {
  const isDevPort =
    window.location.port &&
    window.location.port !== '80' &&
    window.location.port !== '443'
  return window.location.hostname === 'localhost' && isDevPort
    ? `${window.location.protocol}//localhost:8000`
    : window.location.origin
}

// ============================================================================
// Main Composable
// ============================================================================

// ============================================================================
// Module-Level Singleton Socket Instance
// ============================================================================
// CRITICAL: Socket must be shared across ALL components to maintain session
// Moving socket outside useSocket() function makes it a singleton
let socket: Socket | null = null

// Shared reactive state across all components
const state: Ref<SocketState> = ref({
  connected: false,
  connecting: false,
  reconnectAttempts: 0,
  socketId: null,
})

// Shared event handler registry
const eventHandlers = new Map<SocketEventName, Set<Function>>()

/**
 * Vue 3 Composable for Socket.IO connection management
 * Provides reactive state, event listeners, and emit functions
 * Returns the SAME socket instance for all components (singleton pattern)
 */
export function useSocket() {

  // ============================================================================
  // Connection Management
  // ============================================================================

  /**
   * Initialize socket connection
   */
  async function connect(): Promise<void> {
    if (socket?.connected) {
      return
    }

    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('access_token')

      socket = io(getSocketURL(), {
        path: '/socket.io',
        auth: { token },
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: RECONNECT_BASE_DELAY,
        reconnectionDelayMax: RECONNECT_MAX_DELAY,
      })

      state.value.connecting = true

      // Connection success
      socket.on('connect', () => {
        state.value.connected = true
        state.value.connecting = false
        state.value.reconnectAttempts = 0
        state.value.socketId = socket?.id || null

        // Expose socket globally for debugging in dev mode
        // Use: __DEBUG_SOCKET__.emit('debug_give_wilds', {})
        if (import.meta.env.DEV) {
          (window as any).__DEBUG_SOCKET__ = socket
        }

        emit('connect')
        resolve()
      })

      // Connection failed
      socket.on('disconnect', (reason: string) => {
        state.value.connected = false
        state.value.socketId = null
        emit('disconnect', reason)
      })

      // Connection error
      socket.on('connect_error', (_error: Error) => {
        state.value.reconnectAttempts++
        if (state.value.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          state.value.connecting = false
          reject(new Error('Failed to connect to game server'))
        }
      })

      // Register all game event listeners
      registerGameEvents()
    })
  }

  /**
   * Disconnect socket
   */
  function disconnect(): void {
    if (socket) {
      socket.disconnect()
      socket = null
      state.value.connected = false
      state.value.socketId = null
    }
  }

  /**
   * Check if socket is connected
   */
  function isConnected(): boolean {
    return socket?.connected || false
  }

  /**
   * Get raw socket instance (for WebRTC and other advanced use)
   */
  function getSocket(): Socket | null {
    return socket
  }

  // ============================================================================
  // Event Listener Management
  // ============================================================================

  /**
   * Register an event handler
   * Returns cleanup function
   */
  function on(event: SocketEventName, handler: Function): () => void {
    if (!eventHandlers.has(event)) {
      eventHandlers.set(event, new Set())
    }
    eventHandlers.get(event)!.add(handler)

    // Return cleanup function
    return () => {
      eventHandlers.get(event)?.delete(handler)
    }
  }

  /**
   * Remove event listener from mitt emitter
   */
  function off(event: SocketEventName, handler: Function): void {
    eventHandlers.get(event)?.delete(handler)
  }

  /**
   * Emit event to registered handlers
   */
  function emit(event: SocketEventName, ...args: any[]): void {
    const handlers = eventHandlers.get(event)
    if (handlers) {
      handlers.forEach((handler) => handler(...args))
    }
  }

  /**
   * Register all game event listeners on socket
   */
  function registerGameEvents(): void {
    if (!socket) return

    // Game state events
    socket.on('game_state', (data: GameStatePayload) => emit('game_state', data))
    socket.on('game_state_update', (data: GameStatePayload) =>
      emit('game_state_update', data)
    )

    // Room events
    socket.on('room_joined', (data: any) => emit('room_joined', data))
    socket.on('room_created', (data: RoomCreatedPayload) =>
      emit('room_created', data)
    )
    socket.on('game_reconnected', (data: any) => emit('game_reconnected', data))

    // Player events
    socket.on('player_joined', (data: PlayerJoinedPayload) =>
      emit('player_joined', data)
    )
    socket.on('player_left', (data: any) => emit('player_left', data))
    socket.on('player_reconnected', (data: any) =>
      emit('player_reconnected', data)
    )
    socket.on('bot_added', (data: any) => emit('bot_added', data))

    // Game flow events
    socket.on('meld_holder_set', (data: any) => emit('meld_holder_set', data))
    socket.on('game_started', (data: any) => emit('game_started', data))
    socket.on('turn_change', (data: any) => emit('turn_change', data))
    socket.on('round_end', (data: any) => emit('round_end', data))
    socket.on('round_ended', (data: any) => emit('round_ended', data))
    socket.on('game_end', (data: any) => emit('game_end', data))
    socket.on('game_paused', (data: any) => emit('game_paused', data))
    socket.on('paused_game_lobby', (data: any) => emit('paused_game_lobby', data))
    socket.on('paused_lobby_updated', (data: any) => emit('paused_lobby_updated', data))
    socket.on('rematch_started', (data: any) => emit('rematch_started', data))
    socket.on('action_result', (data: ActionResultPayload) =>
      emit('action_result', data)
    )
    socket.on('profile_updated', (data: any) => emit('profile_updated', data))
    socket.on('team_name_updated', (data: any) => emit('team_name_updated', data))
    socket.on('error', (data: ErrorPayload) => emit('error', data))

    // Player activity events (explicit tracking from backend)
    socket.on('player_activity', (data: any) => emit('player_activity', data))

    // Chat events - forward to mitt emitter for GameBoard.vue handlers
    socket.on('chat_message', (data: ChatMessagePayload) => emit('chat_message', data))
    socket.on('chat_history', (data: ChatHistoryPayload) => emit('chat_history', data))

    // Join approval events
    socket.on('join_request', (data: any) => emit('join_request', data))
    socket.on('awaiting_join_approval', (data: any) =>
      emit('awaiting_join_approval', data)
    )
    socket.on('show_seat_selection', (data: any) =>
      emit('show_seat_selection', data)
    )
    socket.on('join_denied', (data: any) => emit('join_denied', data))
    socket.on('join_cancelled', (data: any) => emit('join_cancelled', data))

    // Seat assignment events
    socket.on('seat_assigned', (data: any) => emit('seat_assigned', data))
    socket.on('seat_assignment_complete', (data: any) =>
      emit('seat_assignment_complete', data)
    )
    socket.on('seat_changed', (data: any) => emit('seat_changed', data))
    socket.on('player_kicked', (data: any) => emit('player_kicked', data))
    socket.on('kicked', (data: any) => emit('kicked', data))

    // Spectator events
    socket.on('joined_as_spectator', (data: any) =>
      emit('joined_as_spectator', data)
    )
    socket.on('spectator_joined', (data: any) => emit('spectator_joined', data))
    socket.on('spectator_list', (data: any) => emit('spectator_list', data))
    socket.on('spectator_admitted', (data: any) =>
      emit('spectator_admitted', data)
    )
    socket.on('admitted_to_seat', (data: any) => emit('admitted_to_seat', data))

    // Reconnection events
    socket.on('reconnection_request', (data: any) =>
      emit('reconnection_request', data)
    )
    socket.on('awaiting_reconnection_approval', (data: any) =>
      emit('awaiting_reconnection_approval', data)
    )
    socket.on('reconnection_denied', (data: any) =>
      emit('reconnection_denied', data)
    )

    // Stale game recovery events
    socket.on('stale_game_recovery', (data: any) =>
      emit('stale_game_recovery', data)
    )
    socket.on('recovery_complete', (data: any) =>
      emit('recovery_complete', data)
    )

    // RBAC events
    socket.on('creator_action_request_received', (data: any) =>
      emit('creator_action_request_received', data)
    )
    socket.on('creator_action_response_received', (data: any) =>
      emit('creator_action_response_received', data)
    )

    // Player replacement events
    socket.on('player_replaced_with_bot', (data: any) =>
      emit('player_replaced_with_bot', data)
    )

    // Player disconnect events
    socket.on('player_temporarily_disconnected', (data: any) =>
      emit('player_temporarily_disconnected', data)
    )
    socket.on('player_truly_disconnected', (data: any) =>
      emit('player_truly_disconnected', data)
    )
    socket.on('game_closed_for_all', (data: any) =>
      emit('game_closed_for_all', data)
    )

    // WebRTC events
    socket.on('webrtc_offer', (data: WebRTCOfferPayload) =>
      emit('webrtc_offer', data)
    )
    socket.on('webrtc_answer', (data: WebRTCAnswerPayload) =>
      emit('webrtc_answer', data)
    )
    socket.on('webrtc_ice_candidate', (data: WebRTCIceCandidatePayload) =>
      emit('webrtc_ice_candidate', data)
    )
    socket.on('player_video_state', (data: VideoStatePayload) =>
      emit('player_video_state', data)
    )
    socket.on('player_video_states', (data: VideoStatePayload[]) =>
      emit('player_video_states', data)
    )
  }

  // ============================================================================
  // Room Management Functions
  // ============================================================================

  /**
   * Create a new game room
   */
  async function createRoom(options: CreateRoomOptions): Promise<RoomCreatedPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onRoomCreated = (data: RoomCreatedPayload) => {
        clearTimeout(timeoutId)
        socket?.off('room_created', onRoomCreated)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('room_created', onRoomCreated)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to create room'))
      }

      socket.on('room_created', onRoomCreated)
      socket.on('error', onError)

      socket.emit('create_room', {
        username: options.username,
        user_id: options.userId || null,
        team_name: options.teamName || '',
        display_name: options.displayName || options.username,
        profile_picture: options.profilePicture || '',
        game_mode: options.gameMode || 'humans',
      })

      timeoutId = setTimeout(() => {
        socket?.off('room_created', onRoomCreated)
        socket?.off('error', onError)
        reject(new Error('Timeout creating room'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Join a game room
   * @returns Promise that resolves when backend responds with room data
   *          Returns { awaiting_approval: true } if waiting for creator to approve
   */
  function joinRoom(options: JoinRoomOptions): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      // Track cleanup functions for event bus listeners
      let cleanupFns: Array<() => void> = []
      let timeout: ReturnType<typeof setTimeout>

      const cleanup = () => {
        if (timeout) clearTimeout(timeout)
        cleanupFns.forEach((fn) => fn())
        cleanupFns = []
      }

      const handleSeatAssigned = (data: any) => {
        Logger.info(LogCategory.SOCKET, '[JOIN-3] seat_assigned received:', data)
        cleanup()
        resolve(data)
      }

      const handleRoomCreated = (data: any) => {
        Logger.debug(LogCategory.SOCKET, '[joinRoom] handleRoomCreated:', data)
        cleanup()
        resolve(data)
      }

      const handleGameReconnected = (data: any) => {
        Logger.debug(LogCategory.SOCKET, '[joinRoom] handleGameReconnected:', data)
        cleanup()
        resolve(data)
      }

      const handleAwaitingApproval = (data: any) => {
        // Don't cleanup - keep listeners active for seat_assigned
        Logger.info(LogCategory.SOCKET, '[JOIN-2] Awaiting join approval:', data)
      }

      const handleJoinDenied = (data: any) => {
        Logger.warn(LogCategory.SOCKET, '[joinRoom] Join denied:', data)
        cleanup()
        reject(new Error(data?.message || 'Join request was denied'))
      }

      const handleError = (error: any) => {
        Logger.error(LogCategory.SOCKET, '[joinRoom] Error:', error)
        cleanup()
        reject(new Error(error.message || 'Failed to join room'))
      }

      // Use event bus listeners (more reliable than raw socket if socket reconnects)
      // The event bus is populated by setupSocketListeners which bridges raw socket -> event bus
      cleanupFns.push(on('seat_assigned', handleSeatAssigned))
      cleanupFns.push(on('room_created', handleRoomCreated))
      cleanupFns.push(on('game_reconnected', handleGameReconnected))
      cleanupFns.push(on('awaiting_join_approval', handleAwaitingApproval))
      cleanupFns.push(on('join_denied', handleJoinDenied))
      cleanupFns.push(on('error', handleError))

      Logger.debug(LogCategory.SOCKET, '[joinRoom] Registered event bus listeners for seat_assigned, room_created, etc.')

      // Set up timeout (use 5 minutes to allow for approval flow)
      timeout = setTimeout(() => {
        Logger.warn(LogCategory.SOCKET, '[joinRoom] Join request timed out after 5 minutes')
        cleanup()
        reject(new Error('Join room timeout after 5 minutes'))
      }, 300000)

      // Now emit the join request
      Logger.info(LogCategory.SOCKET, '[JOIN-1] Emitting join_room:', {
        room_code: options.roomCode,
        username: options.username,
        display_name: options.displayName || options.username
      })
      socket.emit('join_room', {
        room_code: options.roomCode,
        username: options.username,
        user_id: options.userId || null,
        display_name: options.displayName || options.username,
        profile_picture: options.profilePicture || '',
      })
    })
  }

  /**
   * Reconnect to an in-progress game
   */
  async function reconnectRoom(options: ReconnectRoomOptions): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onReconnected = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_reconnected', onReconnected)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_reconnected', onReconnected)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to reconnect to game'))
      }

      socket.on('game_reconnected', onReconnected)
      socket.on('error', onError)

      socket.emit('join_room', {
        room_code: options.roomCode,
        username: options.username,
        user_id: options.userId || null,
        profile_picture: options.profilePicture || '',
        reconnect: true,
      })

      timeoutId = setTimeout(() => {
        socket?.off('game_reconnected', onReconnected)
        socket?.off('error', onError)
        reject(new Error('Timeout reconnecting to game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Leave current game room
   */
  function leaveRoom(roomCode: string): void {
    if (!socket?.connected) return
    socket.emit('leave_room', { room_code: roomCode })
  }

  /**
   * Add a bot to the current room
   */
  async function addBot(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onBotAdded = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('bot_added', onBotAdded)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('bot_added', onBotAdded)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to add bot'))
      }

      socket.on('bot_added', onBotAdded)
      socket.on('error', onError)

      socket.emit('add_bot', {})

      timeoutId = setTimeout(() => {
        socket?.off('bot_added', onBotAdded)
        socket?.off('error', onError)
        reject(new Error('Timeout adding bot'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Replace a disconnected player with a bot
   */
  async function replacePlayerWithBot(playerId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onPlayerReplaced = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('player_replaced_with_bot', onPlayerReplaced)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('player_replaced_with_bot', onPlayerReplaced)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to replace player with bot'))
      }

      socket.on('player_replaced_with_bot', onPlayerReplaced)
      socket.on('error', onError)

      socket.emit('replace_player_with_bot', { player_id: playerId })

      timeoutId = setTimeout(() => {
        socket?.off('player_replaced_with_bot', onPlayerReplaced)
        socket?.off('error', onError)
        reject(new Error('Timeout replacing player with bot'))
      }, DEFAULT_TIMEOUT)
    })
  }

  // ============================================================================
  // Game Control Functions
  // ============================================================================

  /**
   * Start the game
   */
  async function startGame(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameStarted = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to start game'))
      }

      socket.on('game_started', onGameStarted)
      socket.on('error', onError)

      socket.emit('start_game', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error('Timeout starting game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Start next round after a round ends
   */
  async function nextRound(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameStarted = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to start next round'))
      }

      socket.on('game_started', onGameStarted)
      socket.on('error', onError)

      socket.emit('next_round', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error('Timeout starting next round'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Restart the game from the beginning
   */
  async function restartGame(force = false): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameStarted = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to restart game'))
      }

      socket.on('game_started', onGameStarted)
      socket.on('error', onError)

      socket.emit('restart_game', { force })

      timeoutId = setTimeout(() => {
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error('Timeout restarting game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * End the current round from menu
   */
  async function menuEndRound(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameStarted = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        resolve(data)
      }

      const onGameEnd = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to end round'))
      }

      socket.on('game_started', onGameStarted)
      socket.on('game_end', onGameEnd)
      socket.on('error', onError)

      socket.emit('menu_end_round', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_started', onGameStarted)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        reject(new Error('Timeout ending round'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * End the game and return all players to lobby
   */
  async function endGame(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameEnd = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to end game'))
      }

      socket.on('game_end', onGameEnd)
      socket.on('error', onError)

      socket.emit('end_game', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_end', onGameEnd)
        socket?.off('error', onError)
        reject(new Error('Timeout ending game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Exit game and return to lobby (game saved)
   */
  async function exitGame(): Promise<ActionResultPayload> {
    return new Promise((resolve) => {
      if (!socket?.connected) {
        resolve({ action: 'exit_game', success: true })
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'exit_game') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (_data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'exit_game', success: true })
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('exit_game', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'exit_game', success: true })
      }, SHORT_TIMEOUT)
    })
  }

  /**
   * Pause the game and save state for later resumption
   * @returns Promise that resolves when backend confirms pause
   */
  async function pauseGame(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGamePaused = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_paused', onGamePaused)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_paused', onGamePaused)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to pause game'))
      }

      socket.on('game_paused', onGamePaused)
      socket.on('error', onError)

      socket.emit('pause_game', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_paused', onGamePaused)
        socket?.off('error', onError)
        reject(new Error('Timeout pausing game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Resume a paused game - opens paused game lobby
   * @returns Promise that resolves with lobby data
   */
  async function resumePausedGame(gameId: string, roomCode?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onPausedLobby = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to resume paused game'))
      }

      socket.on('paused_game_lobby', onPausedLobby)
      socket.on('error', onError)

      socket.emit('resume_paused_game', { game_id: gameId, room_code: roomCode })

      timeoutId = setTimeout(() => {
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        reject(new Error('Timeout resuming paused game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Start a paused game after players have rejoined
   * @returns Promise that resolves when game starts
   */
  async function startPausedGame(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onGameStarted = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to start paused game'))
      }

      socket.on('game_started', onGameStarted)
      socket.on('error', onError)

      socket.emit('start_paused_game', {})

      timeoutId = setTimeout(() => {
        socket?.off('game_started', onGameStarted)
        socket?.off('error', onError)
        reject(new Error('Timeout starting paused game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Replace a missing player with a bot in paused game lobby
   */
  function replaceWithBotPaused(seatPosition: number): void {
    if (!socket?.connected) return
    socket.emit('replace_with_bot_paused', { seat_position: seatPosition })
  }

  /**
   * Join a paused game lobby as a returning player
   */
  async function joinPausedGame(roomCode: string, displayName?: string, profilePicture?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onPausedLobby = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to join paused game'))
      }

      socket.on('paused_game_lobby', onPausedLobby)
      socket.on('error', onError)

      socket.emit('join_paused_game', {
        room_code: roomCode,
        display_name: displayName,
        profile_picture: profilePicture,
      })

      timeoutId = setTimeout(() => {
        socket?.off('paused_game_lobby', onPausedLobby)
        socket?.off('error', onError)
        reject(new Error('Timeout joining paused game'))
      }, DEFAULT_TIMEOUT)
    })
  }

  // ============================================================================
  // Game Action Functions
  // ============================================================================

  /**
   * Draw from stock pile
   */
  async function drawFromStock(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'draw_stock') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to draw from stock'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('draw_from_stock', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout drawing from stock'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Pick up discard pile
   */
  async function pickupPile(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'pickup_pile') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to pick up discard pile'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('pickup_discard_pile', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout picking up discard pile'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Play a meld
   */
  async function playMeld(
    cardIndices: number[],
    isWildMeld = false,
    isHiddenCanasta = false
  ): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'meld') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to play meld'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('play_meld', {
        card_indices: cardIndices,
        is_wild_meld: isWildMeld,
        is_hidden_canasta: isHiddenCanasta,
      })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout playing meld'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Play multiple melds at once (for opening meld requirement)
   */
  async function playMelds(melds: MeldData[]): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'melds') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to play melds'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('play_melds', { melds })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout playing melds'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Add cards to existing meld
   */
  async function addToMeld(
    meldRank: string,
    cardIndices: number[],
    isWildMeld = false
  ): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'add_to_meld') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to add to meld'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('add_to_meld', {
        meld_rank: meldRank,
        card_indices: cardIndices,
        is_wild_meld: isWildMeld,
      })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout adding to meld'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Discard a card
   */
  async function discard(cardIndex: number): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'discard') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to discard'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('discard', { card_index: cardIndex })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout discarding card'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Set this player as the meld holder for their team
   */
  async function setMeldHolder(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onMeldHolderSet = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('meld_holder_set', onMeldHolderSet)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('meld_holder_set', onMeldHolderSet)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to set meld holder'))
      }

      socket.on('meld_holder_set', onMeldHolderSet)
      socket.on('error', onError)

      socket.emit('set_meld_holder', {})

      timeoutId = setTimeout(() => {
        socket?.off('meld_holder_set', onMeldHolderSet)
        socket?.off('error', onError)
        reject(new Error('Timeout setting meld holder'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Go out
   */
  async function goOut(finalCardIndex: number | null = null): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'go_out') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to go out'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('go_out', { final_card_index: finalCardIndex })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout going out'))
      }, DEFAULT_TIMEOUT)
    })
  }

  // ============================================================================
  // Hidden Canasta Functions
  // ============================================================================

  /**
   * Create a hidden wild canasta from selected wild cards
   */
  async function createHiddenCanasta(cardIndices: number[]): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'create_hidden_canasta') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to create hidden canasta'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('create_hidden_canasta', { card_indices: cardIndices })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout creating hidden canasta'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Add cards to an existing hidden canasta
   */
  async function addToHiddenCanasta(cardIndices: number[]): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'add_to_hidden_canasta') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to add to hidden canasta'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('add_to_hidden_canasta', { card_indices: cardIndices })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout adding to hidden canasta'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Play (reveal) the hidden canasta
   */
  async function playHiddenCanasta(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'play_hidden_canasta') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to play hidden canasta'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('play_hidden_canasta', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout playing hidden canasta'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Convert hidden canasta (< 7 cards) to normal wild canasta
   */
  async function convertHiddenCanasta(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'convert_hidden_to_wild') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to convert hidden canasta'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('convert_hidden_to_wild', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout converting hidden canasta'))
      }, DEFAULT_TIMEOUT)
    })
  }

  // ============================================================================
  // Other Game Actions
  // ============================================================================

  /**
   * Pick up foot (second hand) when main hand is empty
   */
  async function pickupFoot(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'pickup_foot') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to pick up foot'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('pickup_foot', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout picking up foot'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Return pile (undo pickup of discard pile)
   */
  async function returnPile(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'return_pile') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to return pile'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('return_pile', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout returning pile'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Undo last play (meld or add-to-meld)
   */
  async function undoLastPlay(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'undo_last_play') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to undo'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('undo_last_play', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout undoing play'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Pass turn (when draw pile is empty)
   */
  async function passTurn(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'pass_turn') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to pass turn'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('pass_turn', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout passing turn'))
      }, DEFAULT_TIMEOUT)
    })
  }

  /**
   * Force end round (when draw pile is empty)
   */
  async function forceEndRound(): Promise<ActionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'force_end_round') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error(data.message || 'Failed to end round'))
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('force_end_round', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        reject(new Error('Timeout ending round'))
      }, DEFAULT_TIMEOUT)
    })
  }

  // ============================================================================
  // Profile & Player Management
  // ============================================================================

  /**
   * Update player profile (display name and/or avatar)
   */
  async function updateProfile(
    displayName: string,
    profilePicture: string
  ): Promise<any> {
    return new Promise((resolve) => {
      if (!socket?.connected) {
        resolve({ success: true })
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onProfileUpdated = (data: any) => {
        clearTimeout(timeoutId)
        socket?.off('profile_updated', onProfileUpdated)
        socket?.off('error', onError)
        resolve(data)
      }

      const onError = (_data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('profile_updated', onProfileUpdated)
        socket?.off('error', onError)
        resolve({ success: true })
      }

      socket.on('profile_updated', onProfileUpdated)
      socket.on('error', onError)

      socket.emit('update_profile', {
        display_name: displayName,
        profile_picture: profilePicture,
      })

      timeoutId = setTimeout(() => {
        socket?.off('profile_updated', onProfileUpdated)
        socket?.off('error', onError)
        resolve({ success: true })
      }, SHORT_TIMEOUT)
    })
  }

  /**
   * Update team name
   * @param teamId - 'A' or 'B'
   * @param name - New team name (max 20 chars)
   */
  function updateTeamName(teamId: string, name: string): void {
    if (!socket?.connected) {
      Logger.warn(LogCategory.SOCKET, 'Cannot update team name: not connected')
      return
    }
    socket.emit('update_team_name', { team_id: teamId, name })
  }

  /**
   * Request restart approval from other players
   */
  async function requestRestart(): Promise<ActionResultPayload> {
    return new Promise((resolve) => {
      if (!socket?.connected) {
        resolve({ action: 'request_restart', success: true })
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'request_restart') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (_data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'request_restart', success: true })
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('request_restart', {})

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'request_restart', success: true })
      }, SHORT_TIMEOUT)
    })
  }

  /**
   * Respond to a restart request
   */
  async function restartResponse(approved: boolean): Promise<ActionResultPayload> {
    return new Promise((resolve) => {
      if (!socket?.connected) {
        resolve({ action: 'restart_response', success: true })
        return
      }

      let timeoutId: ReturnType<typeof setTimeout>

      const onActionResult = (data: ActionResultPayload) => {
        if (data.action === 'restart_response') {
          clearTimeout(timeoutId)
          socket?.off('action_result', onActionResult)
          socket?.off('error', onError)
          resolve(data)
        }
      }

      const onError = (_data: ErrorPayload) => {
        clearTimeout(timeoutId)
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'restart_response', success: true })
      }

      socket.on('action_result', onActionResult)
      socket.on('error', onError)

      socket.emit('restart_response', { approved })

      timeoutId = setTimeout(() => {
        socket?.off('action_result', onActionResult)
        socket?.off('error', onError)
        resolve({ action: 'restart_response', success: true })
      }, SHORT_TIMEOUT)
    })
  }

  // ============================================================================
  // Seat & Spectator Management
  // ============================================================================

  /**
   * Select a seat when joining a room
   * @param seat - The seat number (0-3)
   * @param playerSocketId - Optional socket ID of player being assigned (for creator assigning seat)
   * @param playerUserId - Optional user ID for fallback lookup if socket ID is stale
   */
  function selectSeat(seat: number, playerSocketId?: string, playerUserId?: string): void {
    if (!socket?.connected) return
    const data: Record<string, unknown> = { seat }
    if (playerSocketId) {
      data.player_socket_id = playerSocketId
    }
    if (playerUserId) {
      data.player_user_id = playerUserId
    }
    socket.emit('select_seat', data)
  }

  /**
   * Creator moves a player to a different seat
   * @todo NOT YET USED - Reserved for future seat management UI
   */
  function movePlayerSeat(playerId: string, newSeat: number): void {
    if (!socket?.connected) return
    socket.emit('move_player_seat', {
      player_id: playerId,
      new_seat: newSeat,
    })
  }

  /**
   * Creator kicks a player from the room
   */
  function kickPlayer(playerId: string): void {
    if (!socket?.connected) return
    socket.emit('kick_player', { player_id: playerId })
  }

  /**
   * Request spectator list
   * @todo NOT YET USED - Reserved for future spectator management feature
   */
  function requestSpectatorList(): void {
    if (!socket?.connected) return
    socket.emit('request_spectator_list')
  }

  /**
   * Admit spectator to a seat
   * @todo NOT YET USED - Reserved for future spectator management feature
   */
  function admitSpectator(spectatorId: string, seat: number): void {
    if (!socket?.connected) return
    socket.emit('admit_spectator', { spectator_id: spectatorId, seat })
  }

  // ============================================================================
  // Join Approval Management
  // ============================================================================

  /**
   * Approve a join request
   */
  function approveJoin(sid: string): void {
    if (!socket?.connected) return
    socket.emit('approve_join', { sid })
  }

  /**
   * Deny a join request
   */
  function denyJoin(sid: string): void {
    if (!socket?.connected) return
    socket.emit('deny_join', { sid })
  }

  /**
   * Approve a reconnection request
   */
  function approveReconnection(playerId: string): void {
    if (!socket?.connected) return
    socket.emit('approve_reconnection', { player_id: playerId })
  }

  /**
   * Deny a reconnection request
   */
  function denyReconnection(playerId: string): void {
    if (!socket?.connected) return
    socket.emit('deny_reconnection', { player_id: playerId })
  }

  /**
   * Handle stale game recovery action
   * @param action - 'wait' to wait for players, 'replace_with_bots' to fill with bots
   */
  function staleGameRecoveryAction(action: 'wait' | 'replace_with_bots'): void {
    if (!socket?.connected) return
    socket.emit('stale_game_recovery_action', { action })
  }

  /**
   * Cancel join request
   */
  function cancelJoinRequest(): void {
    if (!socket?.connected) return
    socket.emit('cancel_join_request')
  }

  /**
   * Player ready (after seat assignment)
   */
  function playerReady(): void {
    if (!socket?.connected) return
    socket.emit('player_ready', {})
  }

  // ============================================================================
  // RBAC (Role-Based Access Control)
  // ============================================================================

  /**
   * Request creator action approval
   */
  function creatorActionRequest(action: string, requestId: string): void {
    if (!socket?.connected) return
    socket.emit('creator_action_request', {
      action,
      request_id: requestId,
    })
  }

  /**
   * Respond to creator action request
   */
  function creatorActionResponse(
    requestId: string,
    approved: boolean,
    requesterId: string,
    action: string
  ): void {
    if (!socket?.connected) return
    socket.emit('creator_action_response', {
      request_id: requestId,
      approved,
      requester_id: requesterId,
      action,
    })
  }

  /**
   * Leave game (RBAC)
   */
  function leaveGame(): void {
    if (!socket?.connected) return
    socket.emit('leave_game', {})
  }

  /**
   * Close game for all players (creator only)
   */
  function closeGameForAll(): void {
    if (!socket?.connected) return
    socket.emit('close_game_for_all', {})
  }

  // ============================================================================
  // WebRTC Functions
  // ============================================================================

  /**
   * Emit WebRTC offer to target player
   */
  function sendWebRTCOffer(targetPlayerId: string, offer: RTCSessionDescriptionInit): void {
    if (!socket?.connected) return
    socket.emit('webrtc_offer', {
      target_player_id: targetPlayerId,
      offer,
    })
  }

  /**
   * Emit WebRTC answer to target player
   */
  function sendWebRTCAnswer(
    targetPlayerId: string,
    answer: RTCSessionDescriptionInit
  ): void {
    if (!socket?.connected) return
    socket.emit('webrtc_answer', {
      target_player_id: targetPlayerId,
      answer,
    })
  }

  /**
   * Emit ICE candidate to target player
   */
  function sendWebRTCIceCandidate(
    targetPlayerId: string,
    candidate: RTCIceCandidateInit
  ): void {
    if (!socket?.connected) return
    socket.emit('webrtc_ice_candidate', {
      target_player_id: targetPlayerId,
      candidate,
    })
  }

  /**
   * Emit video state change
   */
  function videoStateChanged(videoEnabled: boolean, audioEnabled: boolean): void {
    if (!socket?.connected) return
    socket.emit('video_state_changed', {
      video_enabled: videoEnabled,
      audio_enabled: audioEnabled,
    })
  }

  /**
   * Request video states from all players
   */
  function requestVideoStates(): void {
    if (!socket?.connected) return
    socket.emit('request_video_states')
  }

  // ============================================================================
  // Debug Functions
  // ============================================================================

  /**
   * Toggle debug mode - shows bot hands for testing bot logic
   * @todo NOT YET USED - Reserved for future debug panel UI
   */
  function toggleDebugMode(enabled: boolean): void {
    if (!socket?.connected) return
    socket.emit('toggle_debug_mode', { enabled })
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  // NOTE: Socket cleanup on component unmount is DISABLED to prevent
  // disconnection when switching components (e.g., GameLobby → GameBoard).
  // Socket persists across the session. Disconnect only on logout or tab close.

  // ============================================================================
  // Direct Socket Emit (for components that need raw socket access)
  // ============================================================================

  /**
   * Emit an event directly to the socket server
   * Use this when you need to send custom events not covered by the action functions
   */
  function socketEmit(event: string, data?: any): void {
    socket?.emit(event, data)
  }

  // ============================================================================
  // Chat
  // ============================================================================

  /**
   * Send a chat message to all players in the room
   * @param message - Text message (can be empty if gif is provided)
   * @param gif - Optional GIF attachment
   */
  function sendChatMessage(message: string, gif?: ChatGifData): void {
    if (!socket?.connected) return
    socket.emit('send_chat_message', { message, gif })
  }

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // State
    state,

    // Connection
    connect,
    disconnect,
    isConnected,
    getSocket,
    socketEmit,

    // Event listeners
    on,
    off,

    // Room management
    createRoom,
    joinRoom,
    reconnectRoom,
    leaveRoom,
    addBot,
    replacePlayerWithBot,

    // Game control
    startGame,
    nextRound,
    restartGame,
    menuEndRound,
    endGame,
    exitGame,
    pauseGame,
    resumePausedGame,
    startPausedGame,
    replaceWithBotPaused,
    joinPausedGame,

    // Game actions
    drawFromStock,
    pickupPile,
    playMeld,
    playMelds,
    addToMeld,
    discard,
    setMeldHolder,
    goOut,

    // Hidden canasta
    createHiddenCanasta,
    addToHiddenCanasta,
    playHiddenCanasta,
    convertHiddenCanasta,

    // Other actions
    pickupFoot,
    returnPile,
    undoLastPlay,
    passTurn,
    forceEndRound,

    // Profile & player
    updateProfile,
    updateTeamName,
    requestRestart,
    restartResponse,

    // Seat & spectator
    selectSeat,
    movePlayerSeat,
    kickPlayer,
    requestSpectatorList,
    admitSpectator,

    // Join approval
    approveJoin,
    denyJoin,
    approveReconnection,
    denyReconnection,
    staleGameRecoveryAction,
    cancelJoinRequest,
    playerReady,

    // RBAC
    creatorActionRequest,
    creatorActionResponse,
    leaveGame,
    closeGameForAll,

    // WebRTC
    sendWebRTCOffer,
    sendWebRTCAnswer,
    sendWebRTCIceCandidate,
    videoStateChanged,
    requestVideoStates,

    // Debug
    toggleDebugMode,

    // Chat
    sendChatMessage,
  }
}
